import { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../components/contextProvider/ContextProvider";
import UserDao from "../dao/UserDao";
import {
    labelSchoolYearOption,
    resolveInitialSchoolYearKey,
    buildOverviewSyParam,
    filterStartedSchoolYears,
    filterCurrentAndPreviousSchoolYears,
    readAndClearOverviewSchoolYearHandoff
} from "../components/teacherOverview/overviewSchoolYear";

const coerceSchoolYearKey = (value) => {
    if (value == null || value === "") return undefined;
    const asNumber = Number(value);
    return Number.isNaN(asNumber) ? value : asNumber;
};

const useOverviewSchoolYear = () => {
    const { state } = useContext(GlobalContext) || {};
    const { token, schoolYearDto } = state || {};

    // Read handoff once and seed selectedKey immediately so the first overview fetch
    // uses the drilled-in year (avoids a current-year fetch that can race and clobber).
    const [handoffKey] = useState(() => readAndClearOverviewSchoolYearHandoff());
    const [years, setYears] = useState([]);
    const [selectedKey, setSelectedKey] = useState(() => coerceSchoolYearKey(handoffKey) ?? schoolYearDto?.key);

    useEffect(() => {
        if (!token) return undefined;
        let cancelled = false;
        UserDao({ action: "schoolYearsRead", token })
            .then((response) => {
                if (cancelled) return;
                const raw = response?.data?.payload;
                const payload = filterCurrentAndPreviousSchoolYears(
                    filterStartedSchoolYears(Array.isArray(raw) ? raw : [])
                );
                setYears(payload);
                const activeFromList = payload.find((y) => y.active)?.key;
                const activeKey = activeFromList ?? schoolYearDto?.key;
                setSelectedKey(
                    resolveInitialSchoolYearKey({
                        years: payload,
                        handoffKey,
                        activeKey
                    })
                );
            })
            .catch((error) => {
                console.error(error);
                if (!cancelled) {
                    setYears([]);
                    setSelectedKey(coerceSchoolYearKey(handoffKey) ?? schoolYearDto?.key);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [token, schoolYearDto?.key, handoffKey]);

    const activeKey = useMemo(
        () => years.find((y) => y.active)?.key ?? schoolYearDto?.key,
        [years, schoolYearDto?.key]
    );

    const options = useMemo(() => years.map((y) => ({ value: y.key, label: labelSchoolYearOption(y) })), [years]);

    const sy = buildOverviewSyParam({ selectedKey, activeKey });

    return {
        years,
        selectedKey,
        setSelectedKey,
        activeKey,
        isHistorical: sy != null,
        options,
        sy
    };
};

export default useOverviewSchoolYear;
